import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useTranslation } from 'hooks';
import { Loader, Section } from 'components/styledComponents';
import { Step1, Step2, Step3, Step4 } from './forms';
import AppDataLoader from 'components/AppDataLoader';
import Dialog from 'components/Dialog';
import { useParams, useNavigate } from 'react-router-dom';
import { Text, Image } from './style';
import enDemo from './images/atreus-portal-en.jpg';
import deDemo from './images/atreus-portal-de.jpg';

const initialValues = {
  birthDate: '',
  mobilePhone1: '',
  primaryEmail: '',
  nationality: '',
  nameAffix: '',
  firstName: '',
  lastName: '',
  title: '',
  salutation: '',
  primaryAddress: {
    street: '',
    city: '',
    country: '',
    postalCode: ''
  },
  industryFocuses: [],
  specialistFocuses: [],
  managementMethods: [],
  newApplicantQuestions: {
    question1: null,
    question2: null,
    question3: null,
    question4: null,
    question5: null,
    question6: null,
    question7: null,
    question8: null
  },
  documents: []
};

function RegistrationPage({
  language,
  loading,
  syncErrors,
  syncing,
  onSave,
  onStepOneNext,
  isNewUserEmail,
  registerStatus,
  isAutomaticRelease
}) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [comingBack, setComingBack] = useState(true);
  const [canGoNext, setCanGoNext] = useState(true);
  const [userID, setUserID] = useState(null);
  const [syncInitiated, setSyncInitiated] = useState(false);
  const [documents, setDocuments] = useState([
    { subcategory: language === '100' ? '400' : '500', file: null }
  ]);

  const { useInput, handleAdd, handleSet, handleSubmit, registrationValidate } = useForm({
    initialValues: useMemo(() => ({ initialValues, language }), [language]),
    validationSchema: 'registrationPageSchema',
    onSubmit: onSave
  });

  const { value: languageValue, onChange: onlanguageChange } = useInput('language');

  useEffect(() => {
    handleSet('documents', documents);
  }, [documents]);

  useEffect(() => {
    if (id) {
      try {
        const decoded = window.atob(id);
        const parsed = JSON.parse(decoded);
        parsed?.id && setUserID(parsed.id);
      } catch {
        setUserID(null);
      }
    }
    localStorage.setItem('termsAccepted', false);
    localStorage.setItem('privacyAccepted', false);
  }, [id]);

  const handleClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (syncInitiated && !syncing) {
      !syncErrors && handleClose();
      setSyncInitiated(false);
    }
  }, [handleClose, syncErrors, syncing, syncInitiated]);

  const _next = useCallback(() => {
    setComingBack(false);
    setCurrentStep(prev => (prev >= 4 ? 4 : prev + 1));
  }, []);

  const _prev = useCallback(() => {
    setComingBack(true);
    setCurrentStep(prev => (prev <= 1 ? 1 : prev - 1));
  }, []);

  const onStepNext = event => {
    event.preventDefault();
    handleAdd('userID', userID);
    const isValidated = registrationValidate('stepOneRegisterSchema', onStepOneNext);
    setCanGoNext(isValidated !== false);
    setComingBack(currentStep > 2);
  };

  useEffect(() => {
    if (canGoNext && isNewUserEmail === 'success') {
      const timer = setTimeout(() => {
        if (currentStep === 2 && !comingBack) {
          _next();
        }
        setCanGoNext(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [canGoNext, isNewUserEmail, currentStep, comingBack, _next]);

  const handleLanguageChange = useCallback(
    event => {
      onlanguageChange(event);
    },
    [onlanguageChange]
  );

  const automaticReleaseHeader = useCallback(
    node => {
      if (node) node.innerHTML = t('message.automaticReleaseHeader');
    },
    [t]
  );

  const automaticReleaseFooter = useCallback(
    node => {
      if (node) node.innerHTML = t('message.automaticReleaseFooter');
    },
    [t]
  );

  return (
    <>
      <AppDataLoader />
      
        <Step1 currentStep={currentStep} handleNext={_next} />
        <Step2
          currentStep={currentStep}
          documents={documents}
          loading={loading}
          syncing={syncing}
          useInput={useInput}
          handlePrev={_prev}
          handleLanguageChange={handleLanguageChange}
          onStepNext={onStepNext}
          isNewUserEmail={isNewUserEmail}
          setDocuments={setDocuments}
        />
        <Step3
          currentStep={currentStep}
          handleClose={handleClose}
          loading={loading}
          syncing={syncing}
          useInput={useInput}
          handleNext={_next}
          handlePrev={_prev}
          handleSubmit={handleSubmit}
          handleLanguageChange={handleLanguageChange}
        />
        <Step4
          currentStep={currentStep}
          useInput={useInput}
          handlePrev={_prev}
          loading={loading}
          syncing={syncing}
          handleSubmit={handleSubmit}
        />

        {registerStatus === 'success' && (
          <Dialog
            actionLabel={t('button.ok')}
            disabled={syncing}
            open={true}
            syncing={syncing}
            title={
              isAutomaticRelease
                ? t('label.automaticReleaseSuccess')
                : t('label.manualReleaseSuccess')
            }
            onAction={handleClose}
            onClose={handleClose}
            padding="0px 24px"
          >
            {isAutomaticRelease ? (
              <>
                <Text ref={automaticReleaseHeader} />
                <Image src={language === '200' ? enDemo : deDemo} mt="-14" mb="-19" />
                <Text ref={automaticReleaseFooter} />
              </>
            ) : (
              <h3>{t('message.manualRelease')}</h3>
            )}
          </Dialog>
        )}
      
    </>
  );
}

RegistrationPage.propTypes = {
  className: PropTypes.string,
  language: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onStepOneNext: PropTypes.func,
  isNewUserEmail: PropTypes.string,
  registerStatus: PropTypes.string,
  isAutomaticRelease: PropTypes.bool
};

export default RegistrationPage;
